import { useEffect, useRef } from 'react';

import type { BaselinesPayload, RunningStats, SweepDrawConfig } from '@ui/Types/Gnss';

import {
    formatSweepFrequencyLabel,
    getMaximumFiniteValue,
    getPreferredSweepTraceMode,
    getSweepCursorFrequency,
    getSweepFrequencyDisplayUnit,
    getSweepSampleAtBin,
} from '../math/frequency';
import { getSweepViewportRange } from '../math/layout';

import type { RefObject } from 'react';

const TOOLTIP_THROTTLE_MS = 50;

interface SpectrumTooltipState {
    db: number | null;
    frequencyMHz: number;
    stepMHz: number;
    visibleEndMHz: number;
    visibleStartMHz: number;
    x: number;
    y: number;
}

export interface UseSpectrumTooltipParams {
    readonly baselineChannel: 'rx1' | 'rx2';
    readonly baselineRef?: RefObject<BaselinesPayload | null> | null;
    readonly baselineRowsRef?: RefObject<number[][]> | null;
    readonly containerRef: RefObject<HTMLDivElement | null>;
    readonly cursorRef?: RefObject<{ xContent: number | null } | null>;
    readonly drawConfigRef?: RefObject<SweepDrawConfig | null>;
    readonly frameCounterRef: RefObject<number>;
    readonly leftPadPx: number;
    readonly maxRows: number;
    readonly pxPerMHz: number;
    readonly secondaryRowsRef?: RefObject<number[][]> | null;
    readonly secondaryStatsRef?: RefObject<RunningStats | null>;
    readonly showAverage: boolean;
    readonly showLatest: boolean;
    readonly showPeak: boolean;
    readonly spectrumCanvasRef: RefObject<HTMLCanvasElement | null>;
    readonly statsRef?: RefObject<RunningStats | null>;
    readonly sweepRowsRef?: RefObject<number[][]> | null;
    readonly totalMHz: number;
}

export default function useSpectrumTooltip({
    baselineChannel,
    baselineRef,
    baselineRowsRef,
    containerRef,
    cursorRef,
    drawConfigRef,
    frameCounterRef,
    leftPadPx,
    maxRows,
    pxPerMHz,
    secondaryRowsRef,
    secondaryStatsRef,
    showAverage,
    showLatest,
    showPeak,
    spectrumCanvasRef,
    statsRef,
    sweepRowsRef,
    totalMHz,
}: Readonly<UseSpectrumTooltipParams>): RefObject<HTMLDivElement | null> {
    const tooltipElRef = useRef<HTMLDivElement | null>(null);
    const lastMouseClientRef = useRef<{ clientX: number; clientY: number } | null>(null);
    const tooltipRafIdRef = useRef<number | null>(null);
    const tooltipLastCommitTsRef = useRef<number>(0);
    const tooltipPendingRef = useRef<SpectrumTooltipState | null>(null);

    useEffect(() => {
        const canvas = spectrumCanvasRef.current;
        const container = containerRef.current;

        if (!canvas || !container) {
            return;
        }

        const getLatestValueAtBin = (rows: readonly number[][], binIndex: number | null): number | null => {
            return getSweepSampleAtBin(rows.at(-1) ?? null, binIndex);
        };

        const getAverageValueAtBin = (
            rows: readonly number[][],
            runningStats: RunningStats | null,
            binIndex: number | null,
        ): number | null => {
            const statsValue = getSweepSampleAtBin(runningStats?.avg, binIndex);

            if (statsValue !== null) {
                return statsValue;
            }

            if (binIndex === null || binIndex < 0) {
                return null;
            }

            const rowCountLocal = Math.min(rows.length, maxRows);

            if (rowCountLocal === 0) {
                return null;
            }

            const startRowIndex = Math.max(0, rows.length - rowCountLocal);
            let totalValue = 0;
            let sampleCount = 0;

            for (let rowIndex = startRowIndex; rowIndex < rows.length; rowIndex += 1) {
                const sampleValue = getSweepSampleAtBin(rows[rowIndex], binIndex);

                if (sampleValue === null) {
                    continue;
                }

                totalValue += sampleValue;
                sampleCount += 1;
            }

            if (sampleCount === 0) {
                return null;
            }

            return totalValue / sampleCount;
        };

        const getPeakValueAtBin = (
            rows: readonly number[][],
            runningStats: RunningStats | null,
            binIndex: number | null,
        ): number | null => {
            const statsValue = getSweepSampleAtBin(runningStats?.peak, binIndex);

            if (statsValue !== null) {
                return statsValue;
            }

            if (binIndex === null || binIndex < 0) {
                return null;
            }

            const rowCountLocal = Math.min(rows.length, maxRows);

            if (rowCountLocal === 0) {
                return null;
            }

            const startRowIndex = Math.max(0, rows.length - rowCountLocal);
            let maxValue: number | null = null;

            for (let rowIndex = startRowIndex; rowIndex < rows.length; rowIndex += 1) {
                const sampleValue = getSweepSampleAtBin(rows[rowIndex], binIndex);

                if (sampleValue === null) {
                    continue;
                }

                if (maxValue === null || sampleValue > maxValue) {
                    maxValue = sampleValue;
                }
            }

            return maxValue;
        };

        const getBaselineValueAtBin = (binIndex: number | null): number | null => {
            const baselineRows = baselineRowsRef?.current;

            if (baselineRows && baselineRows.length > 0) {
                return getSweepSampleAtBin(baselineRows.at(-1) ?? null, binIndex);
            }

            const baselinePayload = baselineRef?.current;
            const baselineValues = baselineChannel === 'rx2' ? baselinePayload?.rx2 : baselinePayload?.rx1;

            return getSweepSampleAtBin(baselineValues, binIndex);
        };

        function hideTooltip() {
            tooltipPendingRef.current = null;

            if (tooltipRafIdRef.current !== null) {
                cancelAnimationFrame(tooltipRafIdRef.current);
                tooltipRafIdRef.current = null;
            }

            if (tooltipElRef.current) {
                tooltipElRef.current.style.display = 'none';
            }
        }

        function commitTooltip() {
            if (tooltipRafIdRef.current !== null) {
                return;
            }

            tooltipRafIdRef.current = requestAnimationFrame(() => {
                const now = performance.now();
                const last = tooltipLastCommitTsRef.current;

                if (now - last < TOOLTIP_THROTTLE_MS) {
                    tooltipRafIdRef.current = null;
                    commitTooltip();
                    return;
                }

                const next = tooltipPendingRef.current;
                const tooltipElement = tooltipElRef.current;

                if (!next || !tooltipElement) {
                    tooltipRafIdRef.current = null;
                    return;
                }

                const tooltipUnit = getSweepFrequencyDisplayUnit({
                    stepMHz: next.stepMHz,
                    visibleEndMHz: next.visibleEndMHz,
                    visibleStartMHz: next.visibleStartMHz,
                });
                const frequencyLabel = `${formatSweepFrequencyLabel({
                    frequencyMHz: next.frequencyMHz,
                    stepMHz: next.stepMHz,
                    unit: tooltipUnit,
                })} ${tooltipUnit.label}`;
                const valueLabel = typeof next.db === 'number' ? `${next.db.toFixed(2)} dB` : '-- dB';

                tooltipElement.style.left = (next.x + 8).toString() + 'px';
                tooltipElement.style.top = (next.y - 16).toString() + 'px';
                tooltipElement.textContent = `${frequencyLabel}, ${valueLabel}`;
                tooltipElement.style.display = 'block';
                tooltipLastCommitTsRef.current = now;
                tooltipRafIdRef.current = null;
            });
        }

        const resolveTooltipState = (clientX: number, clientY: number): SpectrumTooltipState => {
            const canvasRect = canvas.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const contentX = container.scrollLeft + (clientX - canvasRect.left);
            const cursorFrequency = getSweepCursorFrequency({
                contentX,
                drawConfig: drawConfigRef?.current ?? null,
                leftPadPx,
                pxPerMHz,
                totalMHz,
            });
            const viewportRange = getSweepViewportRange({
                leftPadPx,
                pxPerMHz,
                scrollLeft: container.scrollLeft,
                totalMHz,
                viewportWidth: container.clientWidth,
            });
            const primaryRows = sweepRowsRef?.current ?? [];
            const secondaryRows = secondaryRowsRef?.current ?? [];
            const primaryStats = statsRef?.current ?? null;
            const secondaryStats = secondaryStatsRef?.current ?? null;
            const binIndex = cursorFrequency.binIndex;
            const primaryLatest = showLatest ? getLatestValueAtBin(primaryRows, binIndex) : null;
            const secondaryLatest = showLatest ? getLatestValueAtBin(secondaryRows, binIndex) : null;
            const primaryAverage = showAverage ? getAverageValueAtBin(primaryRows, primaryStats, binIndex) : null;
            const secondaryAverage = showAverage ? getAverageValueAtBin(secondaryRows, secondaryStats, binIndex) : null;
            const primaryPeak = showPeak ? getPeakValueAtBin(primaryRows, primaryStats, binIndex) : null;
            const secondaryPeak = showPeak ? getPeakValueAtBin(secondaryRows, secondaryStats, binIndex) : null;
            const preferredTraceMode = getPreferredSweepTraceMode({
                hasAverage: getMaximumFiniteValue([primaryAverage, secondaryAverage]) !== null,
                hasLatest: getMaximumFiniteValue([primaryLatest, secondaryLatest]) !== null,
                hasPeak: getMaximumFiniteValue([primaryPeak, secondaryPeak]) !== null,
                showAverage,
                showLatest,
                showPeak,
            });
            let traceValue: number | null = null;

            switch (preferredTraceMode) {
                case 'latest': {
                    traceValue = getMaximumFiniteValue([primaryLatest, secondaryLatest]);
                    break;
                }
                case 'average': {
                    traceValue = getMaximumFiniteValue([primaryAverage, secondaryAverage]);
                    break;
                }
                case 'peak': {
                    traceValue = getMaximumFiniteValue([primaryPeak, secondaryPeak]);
                    break;
                }
                default: {
                    traceValue = null;
                    break;
                }
            }

            return {
                db: traceValue ?? getBaselineValueAtBin(binIndex),
                frequencyMHz: cursorFrequency.frequencyMHz,
                stepMHz: cursorFrequency.stepMHz,
                visibleEndMHz: viewportRange.endMHz,
                visibleStartMHz: viewportRange.startMHz,
                x: cursorFrequency.contentX,
                y: clientY - containerRect.top - 10,
            };
        };

        const updateTooltip = (clientX: number, clientY: number) => {
            const nextTooltipState = resolveTooltipState(clientX, clientY);
            tooltipPendingRef.current = nextTooltipState;

            if (cursorRef?.current) {
                cursorRef.current.xContent = nextTooltipState.x;
            }

            frameCounterRef.current = frameCounterRef.current + 1;
            commitTooltip();
        };

        const handleMouseMove = (e: MouseEvent) => {
            lastMouseClientRef.current = { clientX: e.clientX, clientY: e.clientY };
            updateTooltip(e.clientX, e.clientY);
        };

        const handleMouseLeave = () => {
            lastMouseClientRef.current = null;
            hideTooltip();
            if (cursorRef?.current) {
                cursorRef.current.xContent = null;
            }
            frameCounterRef.current = frameCounterRef.current + 1;
        };

        const handleScroll = () => {
            const last = lastMouseClientRef.current;

            if (!last) {
                hideTooltip();
                const cursorState = cursorRef?.current;

                if (cursorState && cursorState.xContent !== null) {
                    cursorState.xContent = null;
                    frameCounterRef.current = frameCounterRef.current + 1;
                }
                return;
            }

            updateTooltip(last.clientX, last.clientY);
        };

        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseleave', handleMouseLeave);
        container.addEventListener('scroll', handleScroll);

        return () => {
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseleave', handleMouseLeave);
            container.removeEventListener('scroll', handleScroll);
            hideTooltip();
        };
    }, [
        baselineChannel,
        baselineRef,
        baselineRowsRef,
        pxPerMHz,
        totalMHz,
        leftPadPx,
        maxRows,
        sweepRowsRef,
        secondaryRowsRef,
        statsRef,
        secondaryStatsRef,
        drawConfigRef,
        cursorRef,
        frameCounterRef,
        showAverage,
        showLatest,
        showPeak,
        containerRef,
        spectrumCanvasRef,
    ]);

    return tooltipElRef;
}
